// Reusable Jobber client picker used by the maintenance scheduling form
// and the Manage Accounts building modal. Searches Jobber clients and
// reports the chosen client + property up to the parent.
//
// Gating (L2/L3 only) is the caller's responsibility — render this only
// when canSeeBillingDetails(role) is true.

import React, { useState } from 'react';
import API_BASE_URL from './config/api';

export function JobberClientPicker({ clientName, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const fieldInput = {
    width: '100%',
    padding: '0.4rem 0.55rem',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: '0.82rem',
    fontFamily: 'inherit',
    background: '#fff',
    boxSizing: 'border-box',
  };
  const smallBtn = {
    background: '#fff', border: '1px solid #7dd3fc', color: '#0369a1',
    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    borderRadius: 6, padding: '0.35rem 0.6rem', whiteSpace: 'nowrap',
  };
  const linkBtn = {
    background: 'transparent', border: 'none', color: '#0369a1',
    fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: 0,
    textDecoration: 'underline',
  };

  const doSearch = async () => {
    setSearching(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/jobber/clients?q=${encodeURIComponent(query)}`);
      const d = await r.json().catch(() => ({}));
      if (d.ok) {
        setResults(d.clients || []);
      } else {
        setResults([]);
        setError(d.error || 'Client search failed.');
      }
    } catch (e) {
      setResults([]);
      setError(e.message || 'Client search failed.');
    } finally {
      setSearching(false);
    }
  };

  const pick = (c) => {
    onPick?.(c);
    setOpen(false);
    setResults([]);
    setQuery('');
    setError('');
  };

  return (
    <div>
      {clientName ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0c4a6e' }}>{clientName}</span>
          <button type="button" onClick={() => setOpen(true)} style={linkBtn}>Change</button>
          {onClear && <button type="button" onClick={onClear} style={linkBtn}>Remove</button>}
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} style={smallBtn}>Attach client…</button>
      )}

      {open && (
        <div style={{ marginTop: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doSearch(); } }}
              placeholder="Search Jobber clients…"
              style={fieldInput}
              autoFocus
            />
            <button type="button" onClick={doSearch} style={smallBtn}>{searching ? '…' : 'Search'}</button>
          </div>
          {error && <div style={{ fontSize: '0.74rem', color: '#b91c1c', marginTop: 4 }}>{error}</div>}
          <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid #f1f5f9', padding: '6px 4px', cursor: 'pointer' }}
              >
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1f2937' }}>{c.name}</div>
                {c.propertyAddress && <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{c.propertyAddress}</div>}
              </button>
            ))}
            {!searching && results.length === 0 && query && !error && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', padding: '4px' }}>No matches.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
